import type { FrontendPHRT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { getRowKeyFromOptionalId, toTableSearchText, type TableRowData } from '@/shared/lib/table';

import type { PatientGlobalSearchHit } from '../types';

type PhrTableKey = 'conditions' | 'labs' | 'medications' | 'notes' | 'procedures' | 'vitals';

const PHR_TABLE_KEYS: readonly PhrTableKey[] = [
  'conditions',
  'labs',
  'medications',
  'notes',
  'procedures',
  'vitals',
];

const PHR_TAB_LABELS: Record<PhrTableKey, string> = {
  conditions: 'Conditions',
  labs: 'Labs',
  medications: 'Medications',
  notes: 'Notes',
  procedures: 'Procedures',
  vitals: 'Vitals',
};

function parseDateMs(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const next = new Date(value).getTime();
  return Number.isFinite(next) ? next : null;
}

function resolvePhrTitle(row: TableRowData): string {
  const candidates = [row['name'], row['title'], row['description']];
  for (const candidate of candidates) {
    const asText = toTableSearchText(candidate).trim();
    if (asText !== '') {
      return asText;
    }
  }
  return 'Untitled record';
}

function resolvePhrSubtitle(tableKey: PhrTableKey, row: TableRowData): string {
  const start = toTableSearchText(row['start']).trim();
  const end = toTableSearchText(row['end']).trim();
  const status = toTableSearchText(row['status']).trim();
  const parts = [PHR_TAB_LABELS[tableKey], status, start, end].filter((part) => part !== '');
  return parts.join(' · ');
}

function resolvePhrSortTimestampMs(row: TableRowData): number | null {
  const startMs = parseDateMs(row['start']);
  if (startMs !== null) {
    return startMs;
  }
  return parseDateMs(row['end']);
}

export function toPhrSearchHits(phr: FrontendPHRT): PatientGlobalSearchHit[] {
  return PHR_TABLE_KEYS.reduce<PatientGlobalSearchHit[]>((allHits, tableKey) => {
    const tableRows = phr[tableKey];
    if (!Array.isArray(tableRows)) {
      return allHits;
    }

    const nextTableHits = tableRows.map((rawRow, index) => {
      const row = rawRow as unknown as TableRowData;
      const rowId = getRowKeyFromOptionalId(row['id'], index, `${tableKey}-row`);
      const title = resolvePhrTitle(row);
      const subtitle = resolvePhrSubtitle(tableKey, row);
      const keywords = Object.values(row).map(toTableSearchText).join(' ').trim();

      return {
        id: `phr:${tableKey}:${rowId}`,
        resource: `phr:${tableKey}` as const,
        tabId: `phr:${tableKey}`,
        title,
        subtitle,
        keywords,
        sortTimestampMs: resolvePhrSortTimestampMs(row),
        resultId: rowId,
      };
    });

    return [...allHits, ...nextTableHits];
  }, []);
}
